import PermissionSetsAPI from 'dashboard/api/permissionSets';

const initialState = () => ({
  accountId: null,
  epoch: 0,
  readVersion: 0,
  records: [],
  catalog: null,
  loading: false,
  pendingWrites: 0,
});

const captureScope = ({ state, rootState }) => {
  const { accountId, epoch } = state;
  return () =>
    state.epoch === epoch &&
    state.accountId === accountId &&
    String(rootState.route.params.accountId) === accountId;
};

const mutate = async (context, request) => {
  const { state, commit, dispatch } = context;
  const isCurrent = captureScope(context);
  if (!isCurrent()) return null;
  const api = new PermissionSetsAPI(state.accountId);
  commit('WRITING', 1);
  try {
    const response = await request(api);
    if (!isCurrent()) return null;
    try {
      await dispatch('load', state.accountId);
    } catch (error) {
      throw Object.assign(error, { permissionSetMutationCompleted: true });
    }
    return isCurrent() ? response.data : null;
  } catch (error) {
    if (isCurrent()) throw error;
    return null;
  } finally {
    if (isCurrent()) commit('WRITING', -1);
  }
};

export default {
  namespaced: true,
  state: initialState,
  mutations: {
    RESET(state) {
      const epoch = state.epoch + 1;
      Object.assign(state, initialState(), { epoch });
    },
    ACCOUNT(state, accountId) {
      state.accountId = accountId;
    },
    READING(state) {
      state.readVersion += 1;
      state.loading = true;
    },
    LOADED(state, { records, catalog }) {
      state.records = records;
      state.catalog = catalog;
    },
    READ_FINISHED(state) {
      state.loading = false;
    },
    WRITING(state, delta) {
      state.pendingWrites += delta;
    },
  },
  actions: {
    reset({ commit }) {
      commit('RESET');
    },
    async load(context, accountId) {
      const { state, rootState, commit } = context;
      const account = String(accountId);
      if (String(rootState.route.params.accountId) !== account) return null;
      if (state.accountId !== account) {
        commit('RESET');
        commit('ACCOUNT', account);
      }
      commit('READING');
      const version = state.readVersion;
      const scopeIsCurrent = captureScope(context);
      const isCurrent = () => scopeIsCurrent() && state.readVersion === version;
      const api = new PermissionSetsAPI(account);
      try {
        const [records, catalog] = await Promise.all([
          api.get(),
          api.catalog(),
        ]);
        if (!isCurrent()) return null;
        commit('LOADED', { records: records.data, catalog: catalog.data });
        return true;
      } catch (error) {
        if (!isCurrent()) return null;
        commit('LOADED', { records: [], catalog: null });
        throw error;
      } finally {
        if (isCurrent()) commit('READ_FINISHED');
      }
    },
    save(context, { id, attributes }) {
      const body = { permission_set: attributes };
      return mutate(context, api =>
        id === undefined ? api.create(body) : api.update(id, body)
      );
    },
    remove(context, id) {
      return mutate(context, api => api.delete(id));
    },
  },
};
